import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';

import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { toast } from 'react-toastify';

import { AITextGenerator, AITextRegenerator } from '../../../API/agent';
import { getSocialMediaPlatform, getPostMediaType } from '../../../helperFunctions/getSurveysWithExtractedFields';

const useStyles = makeStyles(theme => ({
  mainContainer: {
    width: '100%',
    maxWidth: '650px',
    margin: '0 auto 0 auto',
  },
  text: {
    fontWeight: 800,
    fontSize: '17px',
    margin: '0 0 15px 0',
    textAlign: 'left',
    '@media (max-width: 600px)': {
      fontSize: '16px',
      marginInline: 0,
    },
  },
  textInput: {
    padding: '5px 10px',
  },
  textAreaContainer: {
    position: 'relative',
    width: '100%',
    paddingBottom: '15px',
    marginBottom: '22px',
  },
  buttonContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: '20px',
    columnGap: '10px',
  },
  selectButtonSelected: {
    padding: '5px 10px',
    fontWeight: 400,
    border: '2px solid #19989B',
    borderRadius: '4px',
  },
  regenerateButton: {
    padding: '5px 10px',
    fontWeight: 400,
    borderRadius: '4px',
    backgroundColor: '#19989B',
    border: '2px solid #19989B',
    color: 'white',
    '&:hover': {
      backgroundColor: '#19878a',
      color: 'white',
    },
    '&.Mui-disabled': {
      color: 'white',
    },
  },
  aiLoader: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.7)',
  },
  revisionDialogPaper: {
    borderRadius: '12px',
  },
  revisionDialogCloseBtn: {
    position: 'absolute',
    right: 8,
    top: 8,
  },
  revisionSection: {
    marginBottom: 16,
  },
  revisionSectionTitle: {
    fontWeight: 700,
    marginBottom: 6,
  },
  revisionSubtitle: {
    marginBottom: 14,
    color: '#4b5563',
  },
}));

const CAMPAIGN_REGEN_CHANGE_OPTIONS = [
  { key: 'core_message', label: 'Core message / main idea' },
  { key: 'tone_style', label: 'Tone & writing style' },
  { key: 'audience_relevance', label: 'Audience relevance' },
  { key: 'brand_fit', label: 'Brand fit' },
  { key: 'originality', label: 'Originality / distinctiveness' },
  { key: 'cta', label: 'CTA (Call to Action)' },
  { key: 'execution_details', label: 'Execution details / campaign concept' },
];

const TONE_STYLE_OPTIONS = [
  'More premium / elevated',
  'More emotional / human',
  'More bold / provocative',
  'More playful / social',
  'More direct / clear',
  'More strategic / professional',
  'More culturally relevant / trend-driven',
  'Keep similar, but improve execution',
];

const ORIGINALITY_OPTIONS = [
  'Slightly more original',
  'Moderately more distinctive',
  'Highly unconventional / disruptive',
];

const INITIAL_REGEN_FORM = {
  selectedChanges: [],
  coreMessage: '',
  toneStyle: '',
  audienceUpdate: '',
  brandFitUpdate: '',
  originalityLevel: '',
  mainCta: '',
  executionChanges: '',
};

const MultiAiTextArea = props => {
  const appData = useSelector(({ data }) => data);
  const classes = useStyles();

  const initialTextAreaValuesHandler = () => {
    const { id: selectedFieldId } = props.textAreas.find(textAreaField => !textAreaField.ai);
    const initialValue = props.textAreas.map(({ id, ai, text }) => ({
      id,
      ai,
      text,
      value: '',
      isSelected: selectedFieldId === id ? true : false,
    }));
    return initialValue;
  };

  const [textAreaValues, setTextAreaValues] = useState(props.value ? props.value : initialTextAreaValuesHandler());
  const textAreaValuesStateRef = useRef();
  textAreaValuesStateRef.current = textAreaValues;

  const [aiTextLoading, setAiTextLoading] = useState(false);
  const [campaignRegenOpen, setCampaignRegenOpen] = useState(false);
  const [regenForm, setRegenForm] = useState(INITIAL_REGEN_FORM);

  useEffect(() => {
    if (!Array.isArray(props.value) || !props.value.length) return;
    setTextAreaValues(props.value);
  }, [props.value]);

  useEffect(() => {
    const aiGeneratedAnswer = textAreaValues.find(answer => answer.ai);
    if (!aiGeneratedAnswer.value) AITextGeneratorHandler();
  }, []);

  const runAIPitchRequest = (generatorFn, regenerationRequest) => {
    setAiTextLoading(true);

    //Find brand name
    const { Name: personaName } = appData.brandPersonas.find(
      persona => persona.id === props.surveyAnswersState.Post_Brand_Persona,
    );

    const isSocialMedia = props.surveyAnswersState.Post_Media_Type === 1;
    const contentType = getPostMediaType(props.surveyAnswersState);
    const platform = isSocialMedia ? getSocialMediaPlatform(props.surveyAnswersState) : null;

    generatorFn(
      personaName,
      contentType,
      platform,
      props.surveyAnswersState.Post_Title,
      props.surveyAnswersState.Post_Keyword_1,
      props.surveyAnswersState.Post_Keyword_2,
      props.surveyAnswersState.Post_Keyword_3,
      props.surveyAnswersState.Post_Background || props.surveyAnswersState.Post_Client_Brief,
      props.surveyAnswersState.Post_Objective,
      props.surveyAnswersState.Post_Target_Audience,
      props.surveyAnswersState.Post_Customer_Insight,
      props.surveyAnswersState.Post_Key_Message,
      props.surveyAnswersState.Post_RTB,
      props.surveyAnswersState.Post_Creative_Direction,
      props.surveyAnswersState.Post_Budget,
      regenerationRequest,
    )
      .then(updateTextValuesAfterAICall)
      .catch(err => {
        toast.warn('AI could not generate the text', {
          position: 'bottom-right',
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        setAiTextLoading(false);
      });
  };

  const AITextGeneratorHandler = () => {
    runAIPitchRequest(AITextGenerator);
  };

  const AITextRegeneratorHandler = regenerationRequest => {
    runAIPitchRequest(AITextRegenerator, regenerationRequest);
  };

  const updateTextValuesAfterAICall = aiGeneratedText => {
    const updatedTextAreaValues = textAreaValuesStateRef.current.map(teaxtAreaValue => ({
      ...teaxtAreaValue,
      value: teaxtAreaValue.ai ? aiGeneratedText : teaxtAreaValue.value,
    }));
    setTextAreaValues(updatedTextAreaValues);
    props.handleSurveyAnswers(updatedTextAreaValues, props.id);
    setAiTextLoading(false);
  };

  const handleChange = (event, fieldId) => {
    const updatedTextAreaValues = textAreaValues.map(teaxtAreaValue => ({
      ...teaxtAreaValue,
      value: fieldId === teaxtAreaValue.id ? event.target.value : teaxtAreaValue.value,
    }));
    setTextAreaValues(updatedTextAreaValues);
    props.handleSurveyAnswers(updatedTextAreaValues, props.id);
  };

  const selectHandler = fieldId => {
    const updatedTextAreaValues = textAreaValues.map(teaxtAreaValue => ({
      ...teaxtAreaValue,
      isSelected: fieldId === teaxtAreaValue.id,
    }));
    setTextAreaValues(updatedTextAreaValues);
    props.handleSurveyAnswers(updatedTextAreaValues, props.id);
  };

  const regenerateHandler = fieldId => {
    selectHandler(fieldId);
    setRegenForm(INITIAL_REGEN_FORM);
    setCampaignRegenOpen(true);
  };

  const toggleRegenChange = key => {
    setRegenForm(prev => ({
      ...prev,
      selectedChanges: prev.selectedChanges.includes(key)
        ? prev.selectedChanges.filter(item => item !== key)
        : [...prev.selectedChanges, key],
    }));
  };

  const buildRegenerationRequest = () => {
    const aiCurrentPitch = textAreaValuesStateRef.current.find(answer => answer.ai)?.value?.trim() || '';
    const selectedChanges = regenForm.selectedChanges;
    return {
      previousPitch: aiCurrentPitch,
      changeCategories: selectedChanges,
      newCoreMessage: selectedChanges.includes('core_message') ? regenForm.coreMessage.trim() : '',
      toneStyleDirection: selectedChanges.includes('tone_style') ? regenForm.toneStyle : '',
      audienceUpdate: selectedChanges.includes('audience_relevance') ? regenForm.audienceUpdate.trim() : '',
      brandFitUpdate: selectedChanges.includes('brand_fit') ? regenForm.brandFitUpdate.trim() : '',
      originalityLevel: selectedChanges.includes('originality') ? regenForm.originalityLevel : '',
      mainCta: selectedChanges.includes('cta') ? regenForm.mainCta.trim() : '',
      executionConceptChanges: selectedChanges.includes('execution_details') ? regenForm.executionChanges.trim() : '',
    };
  };

  const submitCampaignRegeneration = () => {
    if (!regenForm.selectedChanges.length) {
      toast.warn('Select at least one thing to change before regenerating.');
      return;
    }

    setCampaignRegenOpen(false);
    setRegenForm(INITIAL_REGEN_FORM);
    AITextRegeneratorHandler(buildRegenerationRequest());
  };

  return (
    <Box className={classes.mainContainer}>
      {textAreaValues.map((textArea, index) => (
        <Box
          key={index}
          className={classes.textAreaContainer}
          style={{
            borderBottom: index === textAreaValues.length - 1 ? 'none' : '1px solid lightgray',
          }}>
          {textArea.ai && aiTextLoading ? (
            <Box className={classes.aiLoader}>
              <CircularProgress size={40} style={{ color: '#19989B', marginTop: '-35px' }} />
            </Box>
          ) : null}
          <Typography
            component="div"
            variant="h3"
            className={classes.text}
            style={{
              color: textArea.isSelected ? 'black' : '#C0C0C0',
            }}>
            {textArea.text}
          </Typography>
          <TextField
            fullWidth
            onChange={event => handleChange(event, textArea.id)}
            variant="outlined"
            multiline
            minRows={4}
            disabled={!textArea.isSelected}
            value={textArea.value}
            inputProps={{
              className: classes.textInput,
            }}
          />
          <Box className={classes.buttonContainer}>
            {textArea.ai && (
              <Button
                onClick={() => regenerateHandler(textArea.id)}
                className={classes.regenerateButton}
                disabled={aiTextLoading || !textAreaValues.find(answer => answer.ai)}
                style={
                  !textArea.isSelected ? { backgroundColor: 'white', border: '2px solid #19989B', color: '#19989B' } : {}
                }>
                Regenerate Campaign Pitch
              </Button>
            )}
            <Button
              onClick={() => selectHandler(textArea.id)}
              className={classes.selectButtonSelected}
              style={{
                backgroundColor: textArea.isSelected ? '#19989B' : 'white',
                color: textArea.isSelected ? 'white' : '#19989B',
              }}>
              Select
            </Button>
          </Box>
        </Box>
      ))}
      <Dialog
        open={campaignRegenOpen}
        onClose={() => setCampaignRegenOpen(false)}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.revisionDialogPaper }}>
        <DialogTitle>
          Regenerate Campaign Pitch
          <IconButton aria-label="Close campaign pitch regeneration dialog" className={classes.revisionDialogCloseBtn} onClick={() => setCampaignRegenOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography className={classes.revisionSubtitle}>Tell us what should change in the next version.</Typography>

          <div className={classes.revisionSection}>
            <Typography className={classes.revisionSectionTitle}>What should be changed?</Typography>
            <FormGroup>
              {CAMPAIGN_REGEN_CHANGE_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.key}
                  control={<Checkbox checked={regenForm.selectedChanges.includes(option.key)} onChange={() => toggleRegenChange(option.key)} color="primary" />}
                  label={option.label}
                />
              ))}
            </FormGroup>
          </div>

          {regenForm.selectedChanges.includes('core_message') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>What should the new core message communicate?</Typography>
              <TextField multiline rows={3} fullWidth variant="outlined" value={regenForm.coreMessage} onChange={e => setRegenForm(prev => ({ ...prev, coreMessage: e.target.value }))} />
            </div>
          )}

          {regenForm.selectedChanges.includes('tone_style') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>How should the tone and style change?</Typography>
              <RadioGroup value={regenForm.toneStyle} onChange={e => setRegenForm(prev => ({ ...prev, toneStyle: e.target.value }))}>
                {TONE_STYLE_OPTIONS.map(option => (
                  <FormControlLabel key={option} value={option} control={<Radio color="primary" />} label={option} />
                ))}
              </RadioGroup>
            </div>
          )}

          {regenForm.selectedChanges.includes('audience_relevance') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>Who should this campaign resonate with and what matters to them?</Typography>
              <TextField multiline rows={3} fullWidth variant="outlined" value={regenForm.audienceUpdate} onChange={e => setRegenForm(prev => ({ ...prev, audienceUpdate: e.target.value }))} />
            </div>
          )}

          {regenForm.selectedChanges.includes('brand_fit') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>How should this campaign better reflect the brand?</Typography>
              <TextField multiline rows={3} fullWidth variant="outlined" value={regenForm.brandFitUpdate} onChange={e => setRegenForm(prev => ({ ...prev, brandFitUpdate: e.target.value }))} />
            </div>
          )}

          {regenForm.selectedChanges.includes('originality') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>How different should the new version be?</Typography>
              <RadioGroup value={regenForm.originalityLevel} onChange={e => setRegenForm(prev => ({ ...prev, originalityLevel: e.target.value }))}>
                {ORIGINALITY_OPTIONS.map(option => (
                  <FormControlLabel key={option} value={option} control={<Radio color="primary" />} label={option} />
                ))}
              </RadioGroup>
            </div>
          )}

          {regenForm.selectedChanges.includes('cta') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>What should be the main Call to Action (CTA)?</Typography>
              <TextField multiline rows={2} fullWidth variant="outlined" value={regenForm.mainCta} onChange={e => setRegenForm(prev => ({ ...prev, mainCta: e.target.value }))} />
            </div>
          )}

          {regenForm.selectedChanges.includes('execution_details') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>What should be removed, changed, or added in the execution or campaign concept?</Typography>
              <TextField multiline rows={4} fullWidth variant="outlined" value={regenForm.executionChanges} onChange={e => setRegenForm(prev => ({ ...prev, executionChanges: e.target.value }))} />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignRegenOpen(false)}>Cancel</Button>
          <Button className={classes.regenerateButton} onClick={submitCampaignRegeneration} disabled={aiTextLoading}>
            Regenerate Campaign Pitch
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MultiAiTextArea;
